import {
  AppLayout,
  AppSidebar,
  BundleHeader,
  LoadingPage,
  PageHeader,
  GroupRenderer,
  AlphabetJumper,
  ExportToCSV,
  EmptyState,
  OperatorCardRenderer,
} from "shared/components";
import { Flex } from "styled-system/jsx";
import { ArrowRight, KeyCommand } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { useDirectory } from "./useDirectory";
import {
  AddOperatorModal,
  MyOrganizationSection,
} from "apps/directory/Operator/components";
import { useRef, useState } from "react";
import { getOperatorCSVHeaders } from "shared/utils";
import { DirectoryKey } from "./_directoryAppView.config";
import { useVirtualizer } from "@tanstack/react-virtual";
import { OperatorWithStatus } from "gql/generated";

export const Directory = () => {
  const { t } = useTranslation();
  const {
    alphabetGroups,
    flattenedAlphabetGroups,
    virtualizedHeight,
    appViewSections,
    dateSortedOperators,
    organizationId,
    selectedAppItem,
    showAlphabet,
    onAppViewChange,
    onOperatorClick,
    numOperators,
    searchValue,
    onSearchValueChange,
    loading,
    error,
    hasMore,
    onGetOperatorCsvData,
    isLoadingOperatorCsvData,
  } = useDirectory();
  const [isAddOperatorModalOpen, setIsAddOperatorModalOpen] = useState(false);

  // Setup the virtualizer
  const virtualScrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count:
      selectedAppItem.key === DirectoryKey.All
        ? flattenedAlphabetGroups.length
        : numOperators,
    getScrollElement: () => virtualScrollRef.current,
    estimateSize: () => (selectedAppItem.key === DirectoryKey.All ? 84 : 84),
    overscan: 15,
    scrollMargin: virtualScrollRef.current?.offsetTop ?? 0,
  });

  // Handle the letter click from the alphabet jumper
  const handleLetterClick = (letter: string) => {
    // Find the index of the letter in the flattened alphabet groups and get the item
    const index = flattenedAlphabetGroups.findIndex(
      (group) => group.letter === letter.toLowerCase(),
    );
    const item = flattenedAlphabetGroups[index];
    if (item) {
      // Calculate the offset to scroll to
      // NOTE 412 is the height to the bottomof the bundle header
      const offset =
        item.virtualizerOffset +
        (index === 0
          ? 0
          : item.virtualizerOffset > 412
            ? 412
            : (virtualizer.scrollOffset ?? 0));
      virtualizer.scrollToOffset(offset, {
        align: "start",
        behavior: "smooth",
      });
    }
  };

  const csvFilename = "wb-directory-operators";
  const csvColumnHeaders = getOperatorCSVHeaders(t);

  if (loading) return <LoadingPage />;

  if (error) throw new Error(error.message);

  return (
    <>
      <AddOperatorModal
        isOpen={isAddOperatorModalOpen}
        onClose={() => setIsAddOperatorModalOpen(false)}
        organizationIds={[organizationId]}
      />
      <AppLayout
        ref={virtualScrollRef}
        sidebar={
          <AppSidebar
            Icon={KeyCommand}
            headerTitle={t("directory:HEADER.TITLE")}
            headerDescription={t("directory:HEADER.DESCRIPTION")}
            primaryButtonCopy={t("directory:HEADER.CTA_BUTTON_COPY")}
            primaryButtonProps={{
              visual: "accent.blue",
              onClick: () => setIsAddOperatorModalOpen(!isAddOperatorModalOpen),
            }}
            viewTitle={t("directory:HEADER.ACTIONS.TITLE")}
            viewSections={appViewSections}
            onViewSelectionChange={onAppViewChange}
          />
        }
        content={
          <>
            <PageHeader
              Icon={selectedAppItem.Icon}
              title={t(`directory:HEADER.ACTIONS.${selectedAppItem.key}`)}
              ctaButtonReplacement={
                <ExportToCSV
                  colHeaders={csvColumnHeaders}
                  filename={csvFilename}
                  buttonProps={{
                    mode: "light",
                    width: "auto",
                    isLoading: isLoadingOperatorCsvData,
                  }}
                  onGetData={onGetOperatorCsvData}
                />
              }
            />
            <Flex flexDir={"column"} gap={"300"} mt={"300"}>
              {selectedAppItem.key === "ALL" && (
                <MyOrganizationSection organizationId={organizationId} />
              )}
              <BundleHeader
                searchProps={{
                  value: searchValue,
                  onValueChange: onSearchValueChange,
                }}
                count={numOperators}
                loading={hasMore}
                Icon={selectedAppItem.Icon}
                title={t(`directory:HEADER.ACTIONS.${selectedAppItem.key}`)}
              />
              <Flex
                flexDir="row"
                justifyContent="flex-start"
                alignItems="flex-start"
                gap="400"
              >
                {/* We're using a regular div here so we can easily and dynamically set the style */}
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    height: `${selectedAppItem.key === DirectoryKey.All ? virtualizedHeight : virtualizer.getTotalSize()}px`,
                  }}
                >
                  {numOperators < 1 && (
                    <EmptyState text={t("directory:PAGE.EMPTY")} />
                  )}
                  {selectedAppItem.key === DirectoryKey.All
                    ? virtualizer.getVirtualItems().map((item) => {
                        const alphabetItem =
                          flattenedAlphabetGroups[item.index];
                        const height =
                          alphabetItem.type === "letter" ? "37px" : "84px";

                        return (
                          <div
                            key={item.key}
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height,
                              maxHeight: height,
                              transform: `translateY(${
                                alphabetItem.virtualizerOffset
                              }px)`,
                            }}
                          >
                            {alphabetItem.type === "letter" ? (
                              <GroupRenderer
                                title={alphabetItem.letter.toUpperCase()}
                                showTitle={true}
                              />
                            ) : (
                              OperatorCardRenderer(
                                alphabetItem.operator as OperatorWithStatus,
                                organizationId,
                                t,
                                {
                                  Icon: ArrowRight,
                                  onClick: () =>
                                    onOperatorClick(
                                      alphabetItem.operator as OperatorWithStatus,
                                    ),
                                },
                              )
                            )}
                          </div>
                        );
                      })
                    : virtualizer.getVirtualItems().map((item) => {
                        const operator = dateSortedOperators[item.index];
                        return (
                          <div
                            key={item.key}
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "84px",
                              transform: `translateY(${
                                item.start - virtualizer.options.scrollMargin
                              }px)`,
                            }}
                          >
                            {OperatorCardRenderer(operator, organizationId, t, {
                              Icon: ArrowRight,
                              onClick: () => onOperatorClick(operator),
                            })}
                          </div>
                        );
                      })}
                </div>
                {selectedAppItem.key === "ALL" && showAlphabet && (
                  <AlphabetJumper
                    alphabetGroups={alphabetGroups}
                    jumpBase="GroupRendererAnchor_"
                    mt={"400"}
                    top={"100px"}
                    onLetterClick={handleLetterClick}
                  />
                )}
              </Flex>
            </Flex>
          </>
        }
      />
    </>
  );
};
